<script lang="ts">
    // Shared
    import SharedTopSearchResult from '@amp/web-app-components/src/components/TopSearchResult/TopSearchResult.svelte';

    // Components
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';
    import ClickAction from '~/components/jet/action/ClickAction.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';
    import InteractivePlayButton from '~/components/jet/buttons/InteractivePlayButton.svelte';

    // Stores
    import { getI18n } from '~/stores/i18n';

    export let item;

    let titleHeight: number;
    let isWorkResult = item.headline && item.subtitle;

    const TITLE_HEIGHT_TWO_LINES = 32;
    const TITLE_HEIGHT_THREE_LINES = 48;

    const i18n = getI18n();

    $: ariaLabel = item?.title;
    $: isEllipseArtwork = ['artist', 'composer', 'work'].includes(item.type);
    $: subtitle =
        item.type === 'playlist' && !item.subtitle
            ? $i18n.t('ASE.Web.MusicClassical.Playlist')
            : item.subtitle;
    $: singleLineSubtitle =
        (!item.headline && titleHeight === TITLE_HEIGHT_THREE_LINES) ||
        (isWorkResult && titleHeight === TITLE_HEIGHT_TWO_LINES);
    $: twoLineSubtitle =
        isWorkResult ||
        (!item.headline && titleHeight === TITLE_HEIGHT_TWO_LINES);
</script>

<div class="rectangular-link-lockup" data-testid="rectangular-link-lockup">
    <SharedTopSearchResult {ariaLabel} {isEllipseArtwork}>
        <svelte:fragment slot="artwork" let:artworkProps>
            <div
                class="rectangular-link-lockup__artwork artwork-component"
                class:is-ellipse={isEllipseArtwork}
                data-testid="rectangular-link-artwork"
            >
                <ClickAction action={item.segue} {ariaLabel}>
                    <Artwork
                        artwork={item.artwork}
                        profile={'product'}
                        fallbackKind={item.type}
                        {...artworkProps}
                    />
                </ClickAction>
            </div>

            {#if item.playAction}
                <div class="rectangular-link-lockup__play-button-wrapper">
                    <InteractivePlayButton {item} />
                </div>
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="description-content">
            <ul>
                {#if item.headline}
                    <li
                        class="rectangular-link-lockup__headline"
                        data-testid="rectangular-link-headline"
                    >
                        {item.headline}
                    </li>
                {/if}

                {#if item.title}
                    <li
                        class="rectangular-link-lockup__primary"
                        bind:clientHeight={titleHeight}
                        style:--title-line-clamp={isWorkResult ? 2 : ''}
                        data-testid="rectangular-link-title"
                    >
                        <span class="rectangular-link-lockup__primary__title">
                            {item.title}
                        </span>
                    </li>
                {/if}

                {#if subtitle}
                    <li
                        class="rectangular-link-lockup__secondary"
                        class:single-line-subtitle={singleLineSubtitle}
                        style:--subtitle-line-clamp={twoLineSubtitle ? 2 : ''}
                        data-testid="rectangular-link-subtitle"
                    >
                        {subtitle}
                    </li>
                {/if}
            </ul>
        </svelte:fragment>

        <svelte:fragment slot="primary-action">
            <ClickAction action={item.segue} {ariaLabel} />
        </svelte:fragment>

        <svelte:fragment slot="secondary-buttons">
            <!-- <CloudButtons
                {isPlayable}
                contentDescriptor={item.contentDescriptor}
                iconOnly={true}
                itemProperties={{
                    title: item.title,
                    subtitleLinks: [{ title: subtitleWithoutSeparator }],
                    artwork: item.artwork,
                }}
                buttonStyle="non-platter"
                hideContextualMenu={!item.contentDescriptor}
            /> -->
            {#if item?.contextMenuAction}
                <ProductContextMenu
                    contextMenuAction={item.contextMenuAction}
                    hasMaterial={false}
                    buttonStyle="non-platter"
                />
            {/if}
        </svelte:fragment>
    </SharedTopSearchResult>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;
    @use '@amp/web-shared-styles/app/core/mixins/transgray-scrim' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;

    .rectangular-link-lockup {
        --contextMenuEllipsisFillOverride: var(--systemSecondary);
        --contextMenuEllipsisHoverFillOverride: var(--keyColor);
        padding: 10px 0;
        cursor: pointer;

        // Show play button when hovering over lockup
        &:hover,
        &:has(:focus-visible) {
            --playButtonOpacity: 1;
        }

        // add additional spacing between metadata & contextual button
        // to account for longer lines of metadata
        :global(.top-search-lockup__content) {
            @media (--sidebar-visible) {
                margin-inline-end: 8px;
            }
        }
    }

    .rectangular-link-lockup__artwork {
        border-radius: 10px;
    }

    .artwork-component {
        --aspect-ratio: 1;
        border-radius: var(
            --global-border-radius-medium,
            #{$global-border-radius-medium}
        );
        overflow: hidden;
    }

    .artwork-component.is-ellipse {
        border-radius: 50%;
        overflow: hidden;
    }

    .rectangular-link-lockup__headline {
        color: var(--systemTertiary);
        font: var(--caption-2-emphasized);
    }

    .rectangular-link-lockup__primary {
        @include line-clamp(var(--title-line-clamp, 3));
    }

    .rectangular-link-lockup__primary__title {
        font: var(--title-3-short);
    }

    .rectangular-link-lockup__secondary {
        color: var(--systemSecondary);
        font: var(--callout);

        @include line-clamp(var(--subtitle-line-clamp, 3));
    }

    .rectangular-link-lockup__headline,
    .single-line-subtitle {
        @include line-clamp(1);
    }

    .rectangular-link-lockup__play-button-wrapper {
        --playButtonIconHoverColor: white;
        --playButtonSize: 18px;
        --interactivePlayButtonSize: 18px;
        border-radius: $global-border-radius-medium;
        align-items: center;
        display: flex;
        justify-content: center;
        opacity: var(--playButtonOpacity, 0);
        transition: var(--global-transition);

        @include absolute-center;
        @include transgray-scrim(tracklist);

        @media (--medium) {
            --playButtonSize: 27px;
            --interactivePlayButtonSize: 27px;
        }

        :global(.play-button) {
            border-radius: $global-border-radius-medium;

            @include absolute-center;
        }
    }
</style>
